/**
 * useHomeENOrbHomeGaltonClockLogic
 *
 * [shared]-entity logic hook.
 * Reads/writes the shared-entity store instead of a per-trait
 * reducer — see @almadar/ui/hooks/useSharedEntityStore.
 * Dispatch resolves transitions + effects fully client-side (no
 * server round trip).
 *
 * @generated from schema
 * @trait HomeENOrbHomeGaltonClock
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSharedEntityStoreContext, useSharedEntitySnapshot, runTickFrame, useEventBus, useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { emitCascadeEvent } from '@/hooks/useOrbitalBridge';
import { isFieldValue } from '@almadar/core';
import type { EntityFieldWrite, EntityFrameState, EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import { mergeEntityFrame } from '@almadar/core';
import type { BusEvent } from '@almadar/core';
import type { EventResponse, HomeENGaltonState, HomeENOrbHomeGaltonClockEventPayloadMap } from '@app/shared';

export interface HomeENOrbHomeGaltonClockConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  autoRun: boolean;
  compact: boolean;
  doneLabel: string;
  matchMode: string;
  ofWord: string;
  pauseLabel: string;
  pausedLabel: string;
  playLabel: string;
  resetLabel: string;
  runWhenVisible: boolean;
  runningLabel: string;
  speedLabel: string;
  stepIntervalMs: number;
  stepLabel: string;
  stepWord: string;
  summary: string;
  title: string;
  totalSteps: number;
}

const DEFAULT_HOME_EN_ORB_HOME_GALTON_CLOCK_CONFIG = {
  autoRun: true,
  compact: false,
  doneLabel: "Done",
  matchMode: "",
  ofWord: "of",
  pauseLabel: "Pause",
  pausedLabel: "Paused",
  playLabel: "Play",
  resetLabel: "Reset",
  runWhenVisible: false,
  runningLabel: "Running",
  speedLabel: "Speed",
  stepIntervalMs: 500,
  stepLabel: "Step",
  stepWord: "Step",
  summary: "Balls bounce off pegs and pile into a bell-shaped histogram.",
  title: "",
  totalSteps: 0,
} as HomeENOrbHomeGaltonClockConfig;

type HomeENOrbHomeGaltonClockSlot = 'main';
export interface HomeENOrbHomeGaltonClockLogicReturn {
  state: string;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: string | null;
  slotWriter: Partial<Record<HomeENOrbHomeGaltonClockSlot, string | null>>;
  loading: boolean;
  pendingEvent: string | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeENGaltonState>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeENOrbHomeGaltonClockEventPayloadMap]: (payload?: HomeENOrbHomeGaltonClockEventPayloadMap[K]) => Promise<EventResponse> };
  config: HomeENOrbHomeGaltonClockConfig;
}

export function useHomeENOrbHomeGaltonClockLogic(config?: Partial<HomeENOrbHomeGaltonClockConfig>): HomeENOrbHomeGaltonClockLogicReturn {
  const mergedConfig: HomeENOrbHomeGaltonClockConfig = { ...DEFAULT_HOME_EN_ORB_HOME_GALTON_CLOCK_CONFIG, ...(config ?? {}) };

  // [shared] entity store + coalesced tick driver
  const _sharedStore = useSharedEntityStoreContext();
  const _sharedEntityId = 'HomeENGaltonState';
  _sharedStore.seed(_sharedEntityId, { 'active': false, 'running': false, 'steps': 0, 'elapsedMs': 0, 'done': false, 'histMax': 1.25, 'speed': 1 });
  const _sharedTickEmitsRef = useRef<Array<() => void>>([]);
  const _sharedWritersRef = useRef<ReadonlyArray<(scratch: EntityFrameState) => readonly EntityFieldWrite[]>>([
    // writer HomeENOrbHomeGaltonClock.transportTick
    (scratch: EntityFrameState): readonly EntityFieldWrite[] => {
      const writes: EntityFieldWrite[] = [];
      const frame: Record<string, EventPayloadValue | undefined> = scratch;
      const _emits = _sharedTickEmitsRef.current;
      if (((frame?.active === true) && ((frame?.running === true) && !(frame?.done === true)))) {
        writes.push({ field: 'elapsedMs', value: ((__v) => (isFieldValue(__v) ? __v : undefined))((+(frame?.elapsedMs ?? 0) + 32)) });
        if (((+(frame?.elapsedMs ?? 0) * +(frame?.speed ?? 0)) >= 500)) {
          writes.push({ field: 'elapsedMs', value: ((__v) => (isFieldValue(__v) ? __v : undefined))(0) });
          writes.push({ field: 'steps', value: ((__v) => (isFieldValue(__v) ? __v : undefined))((+(frame?.steps ?? 0) + 1)) });
          _emits.push(((_p) => () => { eventBus.emit('LEARN_STEP', _p, { trait: 'HomeENOrbHomeGaltonClock' }); emitCascadeEvent(eventBus, 'HomeEN', 'HomeENOrbHomeGaltonClock', { event: 'LEARN_STEP', payload: _p, source: { orbital: 'HomeEN', trait: 'HomeENOrbHomeGaltonClock' } }); eventBus.emit('UI:HomeEN.HomeENOrbHomeOrbitSim.LEARN_STEP', _p, { trait: 'HomeENOrbHomeGaltonClock' }); eventBus.emit('UI:HomeEN.HomeENOrbHomeGatesSim.LEARN_STEP', _p, { trait: 'HomeENOrbHomeGaltonClock' }); })({ steps: frame?.steps }));
        }
      }
      return writes;
    },
  ]);

  // Tick: HomeENGaltonStateSharedTick (every 32ms)
  useEffect(() => {
    const interval = setInterval(() => {
      if (true) {
        runTickFrame(_sharedEntityId, _sharedWritersRef.current, _sharedStore);
        for (const _emit of _sharedTickEmitsRef.current.splice(0)) _emit();
      }
    }, 32);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _sharedSnapshot = useSharedEntitySnapshot(_sharedStore, _sharedEntityId);

  const fields = _sharedSnapshot as Partial<HomeENGaltonState>;
  const data: Record<string, EntityRow[]> = { 'HomeENGaltonState': [_sharedSnapshot] };

  // Local per-trait machine state — NOT the shared entity's fields,
  // which always live in the shared store (read fresh on every
  // dispatch, never cached here).
  const [machineState, setMachineState] = useState<string>('idle');
  const [lastPayload, setLastPayload] = useState<EventPayload | undefined>(undefined);
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [slotWriter, setSlotWriter] = useState<Partial<Record<HomeENOrbHomeGaltonClockSlot, string | null>>>({});
  const [error, setError] = useState<string | null>(null);
  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;

  const eventBus = useEventBus();

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['HomeENGaltonState'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT' && (true)) {
      // idle → idle (on INIT)
        _fields['active'] = true;
        fields = { ...(fields ?? {}), 'active': _fields['active'] } as Record<string, EventPayloadValue | undefined>;
        _fields['title'] = "";
        fields = { ...(fields ?? {}), 'title': _fields['title'] } as Record<string, EventPayloadValue | undefined>;
        _fields['running'] = true;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
        _fields['steps'] = 0;
        fields = { ...(fields ?? {}), 'steps': _fields['steps'] } as Record<string, EventPayloadValue | undefined>;
        _fields['elapsedMs'] = 0;
        fields = { ...(fields ?? {}), 'elapsedMs': _fields['elapsedMs'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_UPDATED', _p, { trait: 'HomeENOrbHomeGaltonClock' }); emitCascadeEvent(eventBus, 'HomeEN', 'HomeENOrbHomeGaltonClock', { event: 'LEARN_UPDATED', payload: _p, source: { orbital: 'HomeEN', trait: 'HomeENOrbHomeGaltonClock' } }); eventBus.emit('UI:HomeEN.HomeENOrbHomeOrbitSim.LEARN_UPDATED', _p, { trait: 'HomeENOrbHomeGaltonClock' }); eventBus.emit('UI:HomeEN.HomeENOrbHomeGatesSim.LEARN_UPDATED', _p, { trait: 'HomeENOrbHomeGaltonClock' }); })({ running: fields?.running, steps: fields?.steps }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((payloadObj?.mode === ""))) {
      // idle → idle (on MODE_SELECTED)
        _fields['active'] = true;
        fields = { ...(fields ?? {}), 'active': _fields['active'] } as Record<string, EventPayloadValue | undefined>;
        _fields['title'] = "";
        fields = { ...(fields ?? {}), 'title': _fields['title'] } as Record<string, EventPayloadValue | undefined>;
        _fields['running'] = true;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
        _fields['steps'] = 0;
        fields = { ...(fields ?? {}), 'steps': _fields['steps'] } as Record<string, EventPayloadValue | undefined>;
        _fields['elapsedMs'] = 0;
        fields = { ...(fields ?? {}), 'elapsedMs': _fields['elapsedMs'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_UPDATED', _p, { trait: 'HomeENOrbHomeGaltonClock' }); emitCascadeEvent(eventBus, 'HomeEN', 'HomeENOrbHomeGaltonClock', { event: 'LEARN_UPDATED', payload: _p, source: { orbital: 'HomeEN', trait: 'HomeENOrbHomeGaltonClock' } }); eventBus.emit('UI:HomeEN.HomeENOrbHomeOrbitSim.LEARN_UPDATED', _p, { trait: 'HomeENOrbHomeGaltonClock' }); eventBus.emit('UI:HomeEN.HomeENOrbHomeGatesSim.LEARN_UPDATED', _p, { trait: 'HomeENOrbHomeGaltonClock' }); })({ running: fields?.running, steps: fields?.steps }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((!true && !(payloadObj?.mode === "")))) {
      // idle → idle (on MODE_SELECTED)
        _fields['active'] = false;
        fields = { ...(fields ?? {}), 'active': _fields['active'] } as Record<string, EventPayloadValue | undefined>;
        _fields['running'] = false;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'STEP' && (((fields?.active === true) && !(fields?.done === true)))) {
      // idle → idle (on STEP)
        _fields['steps'] = (+(fields?.steps ?? 0) + 1);
        fields = { ...(fields ?? {}), 'steps': _fields['steps'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_STEP', _p, { trait: 'HomeENOrbHomeGaltonClock' }); emitCascadeEvent(eventBus, 'HomeEN', 'HomeENOrbHomeGaltonClock', { event: 'LEARN_STEP', payload: _p, source: { orbital: 'HomeEN', trait: 'HomeENOrbHomeGaltonClock' } }); eventBus.emit('UI:HomeEN.HomeENOrbHomeOrbitSim.LEARN_STEP', _p, { trait: 'HomeENOrbHomeGaltonClock' }); eventBus.emit('UI:HomeEN.HomeENOrbHomeGatesSim.LEARN_STEP', _p, { trait: 'HomeENOrbHomeGaltonClock' }); })({ steps: fields?.steps }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'TOGGLE_RUN' && ((fields?.active === true))) {
      // idle → idle (on TOGGLE_RUN)
        _fields['running'] = !fields?.running;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_UPDATED', _p, { trait: 'HomeENOrbHomeGaltonClock' }); emitCascadeEvent(eventBus, 'HomeEN', 'HomeENOrbHomeGaltonClock', { event: 'LEARN_UPDATED', payload: _p, source: { orbital: 'HomeEN', trait: 'HomeENOrbHomeGaltonClock' } }); eventBus.emit('UI:HomeEN.HomeENOrbHomeOrbitSim.LEARN_UPDATED', _p, { trait: 'HomeENOrbHomeGaltonClock' }); eventBus.emit('UI:HomeEN.HomeENOrbHomeGatesSim.LEARN_UPDATED', _p, { trait: 'HomeENOrbHomeGaltonClock' }); })({ running: fields?.running, steps: fields?.steps }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'RESET' && ((fields?.active === true))) {
      // idle → idle (on RESET)
        _fields['running'] = true;
        fields = { ...(fields ?? {}), 'running': _fields['running'] } as Record<string, EventPayloadValue | undefined>;
        _fields['steps'] = 0;
        fields = { ...(fields ?? {}), 'steps': _fields['steps'] } as Record<string, EventPayloadValue | undefined>;
        _fields['elapsedMs'] = 0;
        fields = { ...(fields ?? {}), 'elapsedMs': _fields['elapsedMs'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('LEARN_RESET', _p, { trait: 'HomeENOrbHomeGaltonClock' }); emitCascadeEvent(eventBus, 'HomeEN', 'HomeENOrbHomeGaltonClock', { event: 'LEARN_RESET', payload: _p, source: { orbital: 'HomeEN', trait: 'HomeENOrbHomeGaltonClock' } }); eventBus.emit('UI:HomeEN.HomeENOrbHomeOrbitSim.LEARN_RESET', _p, { trait: 'HomeENOrbHomeGaltonClock' }); eventBus.emit('UI:HomeEN.HomeENOrbHomeGatesSim.LEARN_RESET', _p, { trait: 'HomeENOrbHomeGaltonClock' }); })({  }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'SPEED_SET' && ((fields?.active === true))) {
      // idle → idle (on SPEED_SET)
        _fields['speed'] = payloadObj?.speed;
        fields = { ...(fields ?? {}), 'speed': _fields['speed'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeENOrbHomeGaltonClockSlot[]; clear: HomeENOrbHomeGaltonClockSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeENOrbHomeGaltonClockSlot[], clear: [] as HomeENOrbHomeGaltonClockSlot[] };
          case 'MODE_SELECTED': return { write: ['main'] as HomeENOrbHomeGaltonClockSlot[], clear: ['main'] as HomeENOrbHomeGaltonClockSlot[] };
          case 'RESET': return { write: ['main'] as HomeENOrbHomeGaltonClockSlot[], clear: [] as HomeENOrbHomeGaltonClockSlot[] };
          case 'SPEED_SET': return { write: ['main'] as HomeENOrbHomeGaltonClockSlot[], clear: [] as HomeENOrbHomeGaltonClockSlot[] };
          case 'STEP': return { write: ['main'] as HomeENOrbHomeGaltonClockSlot[], clear: [] as HomeENOrbHomeGaltonClockSlot[] };
          case 'TOGGLE_RUN': return { write: ['main'] as HomeENOrbHomeGaltonClockSlot[], clear: [] as HomeENOrbHomeGaltonClockSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event against the shared entity, committing locally
   * first (the fields mirror IS the `[runtime]` row).
   * No server round trip — this trait declares `local`.
   */
  const dispatch = useCallback(async (event: string, payload?: EventPayload): Promise<EventResponse> => {
    const prevState = machineStateRef.current;
    const liveData: Record<string, EntityRow[]> = { 'HomeENGaltonState': [_sharedStore.getSnapshot(_sharedEntityId)] };
    const result = executeTransitionEffects(prevState, event, liveData, payload);
    if (result.newState === undefined) {
      const errorMsg = `No transition defined for ${event} from ${prevState}`;
      setError(errorMsg);
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const writes: EntityFieldWrite[] = Object.entries(result.fields)
      .filter(([, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value: value as FieldValue }));
    if (writes.length > 0) {
      _sharedStore.commit(_sharedEntityId, mergeEntityFrame(_sharedStore.getSnapshot(_sharedEntityId), writes));
    }
    for (const _emit of result.emits) _emit();
    machineStateRef.current = result.newState;
    setMachineState(result.newState);
    const _renderSlots = getRenderUiSlots(prevState, event);
    if (_renderSlots.write.length > 0 || _renderSlots.clear.length > 0) {
      setLastPayload(payload);
      setLastEvent(event);
      setSlotWriter((prev) => ({
        ...prev,
        ...Object.fromEntries(_renderSlots.write.map((s) => [s, event] as const)),
        ...Object.fromEntries(_renderSlots.clear.map((s) => [s, null] as const)),
      }));
    }
    setError(null);
    return { success: true, transitioned: true, states: { 'HomeENOrbHomeGaltonClock': result.newState }, emittedEvents: [] } as EventResponse;
  }, [executeTransitionEffects, getRenderUiSlots, _sharedStore, _sharedEntityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeENOrbHomeGaltonClockEventPayloadMap]: (payload?: HomeENOrbHomeGaltonClockEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'MODE_SELECTED': (payload?: HomeENOrbHomeGaltonClockEventPayloadMap['MODE_SELECTED']) => dispatch('MODE_SELECTED', payload),
    'STEP': () => dispatch('STEP'),
    'TOGGLE_RUN': () => dispatch('TOGGLE_RUN'),
    'RESET': () => dispatch('RESET'),
    'SPEED_SET': (payload?: HomeENOrbHomeGaltonClockEventPayloadMap['SPEED_SET']) => dispatch('SPEED_SET', payload),
    'LEARN_STEP': (payload?: HomeENOrbHomeGaltonClockEventPayloadMap['LEARN_STEP']) => dispatch('LEARN_STEP', payload),
    'LEARN_RESET': () => dispatch('LEARN_RESET'),
    'LEARN_UPDATED': (payload?: HomeENOrbHomeGaltonClockEventPayloadMap['LEARN_UPDATED']) => dispatch('LEARN_UPDATED', payload),
  };

  useUIEvents(enqueueEvent, 'HomeEN.HomeENOrbHomeGaltonClock', ['INIT', 'MODE_SELECTED', 'STEP', 'TOGGLE_RUN', 'RESET', 'SPEED_SET'] as const, eventBus);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'TOGGLE_RUN' (from HomeENOrbHomeGaltonSim) → trigger 'TOGGLE_RUN'
    const unsub_toggle_run_from_homeenorbhomegaltonsim_to_toggle_run = eventBus.on('UI:HomeEN.HomeENOrbHomeGaltonSim.TOGGLE_RUN', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeEN.HomeENOrbHomeGaltonClock', 'TOGGLE_RUN')) return;
      enqueueEvent('TOGGLE_RUN', event.payload);
    });
    // Listen for 'STEP' (from HomeENOrbHomeGaltonSim) → trigger 'STEP'
    const unsub_step_from_homeenorbhomegaltonsim_to_step = eventBus.on('UI:HomeEN.HomeENOrbHomeGaltonSim.STEP', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeEN.HomeENOrbHomeGaltonClock', 'STEP')) return;
      enqueueEvent('STEP', event.payload);
    });
    // Listen for 'RESET' (from HomeENOrbHomeGaltonSim) → trigger 'RESET'
    const unsub_reset_from_homeenorbhomegaltonsim_to_reset = eventBus.on('UI:HomeEN.HomeENOrbHomeGaltonSim.RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeEN.HomeENOrbHomeGaltonClock', 'RESET')) return;
      enqueueEvent('RESET', event.payload);
    });
    return () => {
      if (typeof unsub_toggle_run_from_homeenorbhomegaltonsim_to_toggle_run === 'function') unsub_toggle_run_from_homeenorbhomegaltonsim_to_toggle_run();
      if (typeof unsub_step_from_homeenorbhomegaltonsim_to_step === 'function') unsub_step_from_homeenorbhomegaltonsim_to_step();
      if (typeof unsub_reset_from_homeenorbhomegaltonsim_to_reset === 'function') unsub_reset_from_homeenorbhomegaltonsim_to_reset();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state: machineState,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading: false,
    pendingEvent: null,
    pendingFrom: null,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
